import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { StatusBadge } from '../StatusBadge.js';
import { PlatformModal } from './PlatformModal.js';
import { PlatformReasonModal } from './PlatformReasonModal.js';
import { PlatformConfirmTransitionModal } from './PlatformConfirmTransitionModal.js';
import { PlatformApiError } from '../../lib/platform-auth.js';
import {
  transitionTenantSubscription,
  setTenantDunningExempt,
  getDunningHistory,
  type SubscriptionDetail,
  type DunningEvent,
} from '../../api/platform.js';

/**
 * Operator dunning panel (Phase D-5 PR-2b). Shows the dunning state +
 * timestamps, an exempt toggle, manual transitions (via PR-2a's
 * /subscription/transition endpoint, which validates legality server-side), and
 * the dunning-event history. Sits between the Subscription and Invoices panels.
 */
function fmt(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toISOString().replace('T', ' ').slice(0, 16);
}

const errMsg = (e: unknown): string =>
  e instanceof PlatformApiError ? e.message : 'The action could not be completed.';

type TransitionTarget = 'active' | 'past_due' | 'read_only';

export function DunningPanel({
  tenantId,
  tenantName,
  subscription,
}: {
  tenantId: string;
  tenantName: string;
  subscription: SubscriptionDetail | null;
}): JSX.Element | null {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState<null | { kind: 'transition'; to: TransitionTarget } | { kind: 'exempt'; to: boolean }>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [err, setErr] = useState('');

  const close = (): void => {
    setOpen(null);
    setErr('');
  };
  const onSuccess = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['platform', 'tenant', tenantId] });
    close();
  };

  const transitionMut = useMutation({
    mutationFn: (v: { to: TransitionTarget; reason: string }) =>
      transitionTenantSubscription(tenantId, { toStatus: v.to, reason: v.reason }),
    onSuccess,
  });
  const exemptMut = useMutation({
    mutationFn: (v: { exempt: boolean; reason: string }) =>
      setTenantDunningExempt(tenantId, { exempt: v.exempt, reason: v.reason }),
    onSuccess,
  });

  if (!subscription) return null;
  const s = subscription;

  return (
    <div className="rounded border border-gray-200">
      <h3 className="px-4 py-2 border-b border-gray-100 text-xs uppercase tracking-wide text-gray-500">Dunning</h3>
      <dl className="divide-y divide-gray-100">
        <Row label="State" value={<StatusBadge status={s.status} size="sm" />} />
        {s.pastDueAt ? <Row label="Past due since" value={fmt(s.pastDueAt)} /> : null}
        {/* Grace days are only meaningful in past_due. Key the row off status
            (not `graceDaysRemaining !== null`) so a stray `undefined` from the
            serializer can never leak the literal string "undefined"; `?? '—'`
            is defence-in-depth for a null while past_due (shouldn't occur). */}
        {s.status === 'past_due' ? (
          <Row label="Grace days remaining" value={s.graceDaysRemaining ?? '—'} />
        ) : null}
        {s.readOnlyAt ? <Row label="Read-only since" value={fmt(s.readOnlyAt)} /> : null}
        <Row label="Dunning exempt" value={s.dunningExempt ? 'Yes (cron skips this tenant)' : 'No'} />
      </dl>

      <div className="px-4 py-3 border-t border-gray-100 flex flex-wrap gap-2">
        {(s.status === 'past_due' || s.status === 'read_only') && (
          <button type="button" onClick={() => setOpen({ kind: 'transition', to: 'active' })} className={btnPrimary}>
            Transition to active
          </button>
        )}
        {s.status === 'active' && (
          <button type="button" onClick={() => setOpen({ kind: 'transition', to: 'past_due' })} className={btnSecondary}>
            Transition to past_due
          </button>
        )}
        {s.status === 'past_due' && (
          <button type="button" onClick={() => setOpen({ kind: 'transition', to: 'read_only' })} className={btnSecondary}>
            Transition to read_only
          </button>
        )}
        <button
          type="button"
          onClick={() => setOpen({ kind: 'exempt', to: !s.dunningExempt })}
          className={btnSecondary}
        >
          {s.dunningExempt ? 'Remove dunning exemption' : 'Exempt from dunning'}
        </button>
        <button type="button" onClick={() => setShowHistory(true)} className={btnSecondary}>
          View history
        </button>
      </div>

      {/* Restoration (→ active) is a recovery action: a simple reason modal.
          The worst case is a wrong reason string. No type-to-confirm friction. */}
      <PlatformReasonModal
        open={open?.kind === 'transition' && open.to === 'active'}
        onClose={close}
        title="Transition to active"
        description="Restore the subscription to active."
        confirmLabel="Transition"
        confirmVariant="primary"
        isSubmitting={transitionMut.isPending}
        errorMessage={err}
        onConfirm={async (reason) => {
          if (open?.kind !== 'transition' || open.to !== 'active') return;
          try {
            await transitionMut.mutateAsync({ to: 'active', reason });
          } catch (e) {
            setErr(errMsg(e));
          }
        }}
      />
      {/* Escalations (→ past_due, → read_only) have immediate customer-visible
          consequences (dunning emails / write-access restriction). Require the
          operator to type the tenant's legal name + a reason — guards against an
          accidental click past_due-ing a healthy production tenant. */}
      <PlatformConfirmTransitionModal
        open={open?.kind === 'transition' && open.to !== 'active'}
        onClose={close}
        title={open?.kind === 'transition' && open.to !== 'active' ? `Transition to ${open.to}` : 'Transition'}
        warning={
          open?.kind === 'transition' && open.to === 'past_due'
            ? `This will transition ${tenantName} to past_due. The dunning cron will send a Day 0 email at the next 09:00 UTC tick, the 7-day grace period begins now, and write access is restricted once it elapses unless the subscription is restored.`
            : `This will transition ${tenantName} to read_only immediately — write access (payroll runs, employee adds, leave submissions) is restricted right now, without waiting for the 7-day grace period.`
        }
        expectedConfirmText={tenantName}
        confirmLabel={open?.kind === 'transition' && open.to !== 'active' ? `Transition to ${open.to}` : 'Transition'}
        isSubmitting={transitionMut.isPending}
        errorMessage={err}
        onConfirm={async (reason) => {
          if (open?.kind !== 'transition' || open.to === 'active') return;
          try {
            await transitionMut.mutateAsync({ to: open.to, reason });
          } catch (e) {
            setErr(errMsg(e));
          }
        }}
      />
      <PlatformReasonModal
        open={open?.kind === 'exempt'}
        onClose={close}
        title={open?.kind === 'exempt' && open.to ? 'Exempt from dunning' : 'Remove dunning exemption'}
        description={
          open?.kind === 'exempt' && open.to
            ? 'The dunning cron will skip this tenant entirely (no automated past_due/read_only transitions).'
            : 'The dunning cron will resume evaluating this tenant.'
        }
        confirmLabel="Confirm"
        confirmVariant="primary"
        isSubmitting={exemptMut.isPending}
        errorMessage={err}
        onConfirm={async (reason) => {
          if (open?.kind !== 'exempt') return;
          try {
            await exemptMut.mutateAsync({ exempt: open.to, reason });
          } catch (e) {
            setErr(errMsg(e));
          }
        }}
      />
      {showHistory ? <DunningHistoryModal tenantId={tenantId} onClose={() => setShowHistory(false)} /> : null}
    </div>
  );
}

function DunningHistoryModal({ tenantId, onClose }: { tenantId: string; onClose: () => void }): JSX.Element {
  const { data, isLoading } = useQuery({
    queryKey: ['platform', 'tenant', tenantId, 'dunning-history'],
    queryFn: () => getDunningHistory(tenantId, { limit: 20 }),
  });

  return (
    <PlatformModal
      open
      onClose={onClose}
      title="Dunning history"
      footer={
        <button
          type="button"
          onClick={onClose}
          className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
        >
          Close
        </button>
      }
    >
      {isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : (data?.events.length ?? 0) === 0 ? (
        <p className="text-sm text-gray-500">No dunning history yet.</p>
      ) : (
        <div className="max-h-[60vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                <th className="px-2 py-1.5">Timestamp</th>
                <th className="px-2 py-1.5">Action</th>
                <th className="px-2 py-1.5">Actor</th>
                <th className="px-2 py-1.5">Details</th>
              </tr>
            </thead>
            <tbody>
              {(data?.events ?? []).map((e: DunningEvent) => (
                <tr key={e.id} className="border-t border-gray-100">
                  <td className="px-2 py-1.5 whitespace-nowrap">{fmt(e.createdAt)}</td>
                  <td className="px-2 py-1.5">{actionLabel(e)}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap">{e.actorType}</td>
                  <td className="px-2 py-1.5 text-gray-600">{detail(e)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PlatformModal>
  );
}

function actionLabel(e: DunningEvent): string {
  if (e.action === 'dunning_email_sent') {
    const kind = (e.metadata as { kind?: string } | null)?.kind ?? '';
    return `Dunning email (${kind})`;
  }
  if (e.action.startsWith('transitioned_')) return `→ ${e.action.replace('transitioned_', '')}`;
  if (e.action === 'dunning_exempt_toggled') return 'Dunning exemption changed';
  if (e.action === 'subscription.initialized') return 'Subscription initialized';
  return e.action;
}

function detail(e: DunningEvent): string {
  const m = (e.metadata ?? {}) as { reason?: string; exempt?: boolean; kind?: string };
  if (typeof m.reason === 'string') return m.reason;
  if (typeof m.exempt === 'boolean') return m.exempt ? 'exempt=true' : 'exempt=false';
  return '—';
}

function Row({ label, value }: { label: string; value: ReactNode }): JSX.Element {
  return (
    <div className="px-4 py-2 flex items-center justify-between gap-4 text-sm">
      <dt className="text-gray-500">{label}</dt>
      <dd className="text-gray-900 text-right">{value}</dd>
    </div>
  );
}

const btnSecondary =
  'rounded border border-indigo-deep px-3 py-1.5 text-sm text-indigo-deep hover:bg-indigo-deep hover:text-white';
const btnPrimary = 'rounded bg-indigo-primary px-3 py-1.5 text-sm font-medium text-white hover:opacity-90';
